import { z } from 'zod';
import { chileCalendarDate } from '../common/chile-date.js';
import { isSaleGroupChild } from '../janis/order.js';
/**
 * El plan de pedidos de Janis de una venta del POS (`docs/pedidos-de-tienda.md`
 * §1.4, decidido el 2026-10-28): un pedido, o un GRUPO de pedidos hijos.
 *
 * ## Cuando se agrupa
 *
 * Solo cuando la venta necesita dos pedidos o mas: una parte se entrega en la
 * tienda y otra se despacha o se retira despues, o la venta tiene varias fechas
 * de entrega. En el contrato de `/orders` eso es **una venta con dos envios o
 * mas** (`shipments[]`): cada envio ya es una sola bodega, un modo y una fecha,
 * asi que **un hijo por envio**. El `shipping` legado y una venta con un solo
 * envio son siempre un pedido, como hoy.
 *
 * Este plan NO decide si se agrupa en produccion: con `SALE_GROUPS_ENABLED=false`
 * F1 sigue creando un pedido con varios envios. Solo dice como seria.
 *
 * ## El correlativo `n`
 *
 * Con un orden fijo, para que `n = 1` sea lo primero que sale y un reintento con
 * el mismo cuerpo de los mismos numeros (el `commerceId` de cada hijo es la
 * barrera de idempotencia de F1):
 *
 * 1. el meson (`counter`) primero;
 * 2. despues por fecha ascendente (el dia en Chile: el de la promesa en un
 *    despacho, `promisedAt` en un retiro y `placedAt` en el meson);
 * 3. a igual fecha, por `shipments[].id`, comparado por codigo y no por idioma
 *    (`localeCompare` depende del entorno).
 *
 * El orden de los envios en el cuerpo NO cuenta: barajarlos da el mismo plan.
 *
 * El plan es la fuente de las claves de idempotencia de F1, asi que no confia en
 * que la venta haya pasado por `decoraposOrderSchema`: ids de envio repetidos o
 * un envio sin una sola bodega lanzan `SaleOrdersPlanInputError`, nunca un plan
 * adivinado.
 *
 * ## El `commerceId` del hijo puede chocar: la barrera de F1
 *
 * 🔴 `externalId` es texto libre de 1 a 64, asi que la venta normal
 * `PV-344907-2` y el hijo 2 de la venta `PV-344907` comparten `commerceId`, y
 * la barrera de F1 (buscar por `commerceId` y adoptar lo que encuentra)
 * adoptaria un pedido ajeno. Ademas todo pedido de Janis trae grupo: un pedido
 * normal `V` ya es un grupo con `commerceId` `V`, y Janis asociaria a el los
 * hijos de la venta agrupada `V` (`janisOrderSchema.orderGroupId`).
 *
 * Se adopta un pedido existente solo si su `orderGroupCommerceId` es el numero
 * de la venta (un hijo de ESTA venta: `assertAdoptableSaleChild`), o es igual a
 * su `commerceId` (un pedido normal: `assertAdoptableSingleOrder`). Si no, hay
 * conflicto (`SaleOrderAdoptionConflictError`) y va a intervencion; nunca se
 * adopta. Antes del hijo 1, un pedido con `commerceId` = numero de venta impide
 * agrupar (`assertSaleGroupCommerceIdFree`).
 *
 * Puro: no lee ni escribe nada y no muta la venta.
 */
/**
 * Los rechazos 422 de `POST /decorapos/orders` de una venta que va (o iria) en
 * varios pedidos de Janis (`docs/pedidos-de-tienda.md` §1.4, G1). Van en el
 * borde, antes del message log: el POS los ve en el mostrador. Viajan en `code`.
 *
 * Con `SALE_GROUPS_ENABLED=true` y una venta de dos envios o mas:
 *
 * - `HOLD_GROUP_NOT_SUPPORTED`: retencion de inventario (`inventory.hold`) en
 *   una venta agrupada (el grupo se llamaria como la reserva, pregunta 53).
 * - `GROUP_GLOBAL_DISCOUNT_NOT_SUPPORTED`: un descuento global
 *   (`totals.discount`) no tiene a que hijo ir; va en las lineas.
 * - `GROUP_MULTIPLE_ADDRESSES_NOT_SUPPORTED`: dos direcciones de despacho; la
 *   orden de venta de SAP admite una.
 *
 * Con `SALE_GROUPS_ENABLED=false`:
 *
 * - `MIXED_SALE_REQUIRES_GROUPS`: una venta que mezcla meson o retiro con
 *   despacho necesita dos pedidos.
 *
 * Los de la tienda, y el cuerpo del 422 con todos, en `./order-admission.js`.
 *
 * Un `commerceId` de hijo (`<venta>-<n>`) de mas de 64 caracteres no es un 422
 * sino un 400 de contrato (`order.externalId`), con cuarentena.
 */
export const SALE_GROUP_ADMISSION_CODES = [
    'HOLD_GROUP_NOT_SUPPORTED',
    'GROUP_GLOBAL_DISCOUNT_NOT_SUPPORTED',
    'GROUP_MULTIPLE_ADDRESSES_NOT_SUPPORTED',
    'MIXED_SALE_REQUIRES_GROUPS',
];
export const saleGroupAdmissionCodeSchema = z.enum(SALE_GROUP_ADMISSION_CODES);
/**
 * El cuerpo del 422 (forma de `problemDetails` + `code`). Laxo en las claves:
 * lo emite la API y lo lee el POS, que no tiene que romperse si la API agrega
 * un campo.
 */
export const saleGroupAdmissionProblemSchema = z
    .object({
    title: z.string(),
    status: z.literal(422),
    code: saleGroupAdmissionCodeSchema,
    detail: z.string(),
    correlationId: z.string(),
})
    .passthrough();
/** Largo maximo de un `commerceId` en Janis (`janisOrderCreateRequestSchema`). */
export const SALE_GROUP_COMMERCE_ID_MAX_LENGTH = 64;
/**
 * Como se entrega un hijo: el `method` de su envio (`decoraposShipmentMethodSchema`,
 * `docs/pedidos-de-tienda.md` §1.2).
 */
export const SALE_ORDER_MODES = ['counter', 'pickup', 'delivery'];
/**
 * El `commerceId` de un hijo no cabe en Janis. La venta no se puede agrupar tal
 * como viene: es un rechazo del alta, nunca un `commerceId` recortado (dos
 * ventas recortadas al mismo prefijo compartirian sus hijos).
 */
export class SaleGroupCommerceIdTooLongError extends Error {
    saleExternalId;
    commerceId;
    maxLength = SALE_GROUP_COMMERCE_ID_MAX_LENGTH;
    constructor(saleExternalId, commerceId) {
        super(`El pedido ${commerceId} de la venta ${saleExternalId} tiene ${String(commerceId.length)} ` +
            `caracteres y Janis admite ${String(SALE_GROUP_COMMERCE_ID_MAX_LENGTH)}: la venta no se ` +
            'puede agrupar con ese numero.');
        this.saleExternalId = saleExternalId;
        this.commerceId = commerceId;
        this.name = 'SaleGroupCommerceIdTooLongError';
    }
}
/**
 * La venta no cumple lo que `decoraposOrderSchema` exige de sus envios, y el
 * plan no adivina: es un error de quien llama (una venta sin validar), no un
 * caso del negocio.
 */
export class SaleOrdersPlanInputError extends Error {
    saleExternalId;
    shipmentId;
    reason;
    constructor(saleExternalId, shipmentId, reason) {
        super(reason === 'duplicate_shipment_id'
            ? `La venta ${saleExternalId} repite el envio ${shipmentId}: no se puede planificar.`
            : `El envio ${shipmentId} de la venta ${saleExternalId} no tiene una sola bodega: ` +
                'no se puede planificar.');
        this.saleExternalId = saleExternalId;
        this.shipmentId = shipmentId;
        this.reason = reason;
        this.name = 'SaleOrdersPlanInputError';
    }
}
/**
 * El plan de pedidos de Janis de la venta: `single` con menos de dos envios, o
 * `group` con un hijo por envio, numerados con el orden fijo del encabezado.
 *
 * Lanza `SaleGroupCommerceIdTooLongError` si el `commerceId` de algun hijo pasa
 * de 64 caracteres, y `SaleOrdersPlanInputError` con ids de envio repetidos o
 * un envio sin una sola bodega.
 */
export function planSaleOrders(order) {
    const shipments = order.shipments ?? [];
    if (shipments.length < 2) {
        return { kind: 'single' };
    }
    const ids = new Set();
    for (const shipment of shipments) {
        if (ids.has(shipment.id)) {
            throw new SaleOrdersPlanInputError(order.externalId, shipment.id, 'duplicate_shipment_id');
        }
        ids.add(shipment.id);
    }
    const children = shipments
        .map((shipment) => unnumberedChild(order, shipment))
        .sort(compareChildren)
        .map((child, index) => {
        const n = index + 1;
        const commerceId = `${order.externalId}-${String(n)}`;
        if (commerceId.length > SALE_GROUP_COMMERCE_ID_MAX_LENGTH) {
            throw new SaleGroupCommerceIdTooLongError(order.externalId, commerceId);
        }
        return { n, commerceId, ...child };
    });
    return { kind: 'group', groupCommerceId: order.externalId, children };
}
function unnumberedChild(order, shipment) {
    const lineNumbers = [...shipment.lineNumbers].sort((a, b) => a - b);
    const warehouses = new Set(order.lines
        .filter((line) => lineNumbers.includes(line.lineNumber))
        .map((line) => line.sapWarehouseCode));
    const [warehouse] = warehouses;
    // `/orders` exige una sola bodega por envio; si no la hay, no se adivina.
    if (warehouses.size !== 1 || warehouse === undefined) {
        throw new SaleOrdersPlanInputError(order.externalId, shipment.id, 'shipment_warehouse');
    }
    return {
        shipmentId: shipment.id,
        lineNumbers,
        sapWarehouseCode: warehouse,
        shipDate: chileCalendarDate(new Date(shipmentDay(order, shipment))),
        mode: shipment.method,
    };
}
/**
 * El instante que fija el dia de entrega del envio en Chile:
 *
 * - despacho: el inicio de la ventana prometida, el mismo instante que F1
 *   manda como `deliveryEstimateDate`;
 * - retiro: `promisedAt`, el dia en que el cliente viene;
 * - meson: `placedAt`, porque se entrega en la venta.
 */
function shipmentDay(order, shipment) {
    switch (shipment.method) {
        case 'delivery':
            return shipment.deliveryPromise.start;
        case 'pickup':
            return shipment.promisedAt;
        case 'counter':
            return order.placedAt;
    }
}
/** El meson primero. */
function modeRank(mode) {
    return mode === 'counter' ? 0 : 1;
}
/** Comparacion por codigo de las cadenas: no depende del idioma del entorno. */
function compareText(a, b) {
    if (a === b) {
        return 0;
    }
    return a < b ? -1 : 1;
}
function compareChildren(a, b) {
    return (modeRank(a.mode) - modeRank(b.mode) ||
        compareText(a.shipDate, b.shipDate) ||
        compareText(a.shipmentId, b.shipmentId));
}
/**
 * La barrera de F1 encontro en Janis un pedido con el `commerceId` buscado que
 * NO es de esta venta (ver el encabezado). Nunca se adopta ni se crea encima:
 * va a intervencion, con los datos para que una persona lo resuelva.
 */
export class SaleOrderAdoptionConflictError extends Error {
    janisOrderId;
    commerceId;
    orderGroupCommerceId;
    expectedGroupCommerceId;
    reason;
    constructor(
    /** El pedido que se encontro en Janis. */
    janisOrderId, commerceId, orderGroupCommerceId, 
    /** El numero de la venta agrupada esperada; `null` para una venta normal. */
    expectedGroupCommerceId, reason) {
        super(`El pedido ${janisOrderId} de Janis (commerceId ${String(commerceId)}, grupo ` +
            `${String(orderGroupCommerceId)}) no es de esta venta` +
            (expectedGroupCommerceId === null ? '' : ` (${expectedGroupCommerceId})`) +
            `: no se adopta (${reason}).`);
        this.janisOrderId = janisOrderId;
        this.commerceId = commerceId;
        this.orderGroupCommerceId = orderGroupCommerceId;
        this.expectedGroupCommerceId = expectedGroupCommerceId;
        this.reason = reason;
        this.name = 'SaleOrderAdoptionConflictError';
    }
}
function adoptionConflict(existing, expectedGroupCommerceId, reason) {
    return new SaleOrderAdoptionConflictError(existing.id, existing.commerceId ?? null, existing.orderGroupCommerceId ?? null, expectedGroupCommerceId, reason);
}
/**
 * Un HIJO de la venta agrupada `groupCommerceId` encontro `existing` por su
 * `commerceId` (`<venta>-<n>`): se adopta solo si ya es hijo de ESTA venta
 * (`orderGroupCommerceId === groupCommerceId`), que es el reintento. Un pedido
 * normal que se llama igual, el hijo de otra venta o un pedido sin grupo lanzan
 * `SaleOrderAdoptionConflictError`. Pura.
 */
export function assertAdoptableSaleChild(existing, groupCommerceId) {
    if (existing.orderGroupCommerceId !== groupCommerceId) {
        throw adoptionConflict(existing, groupCommerceId, 'not_a_child_of_this_sale');
    }
}
/**
 * Una venta NORMAL (un pedido) encontro `existing` por su `commerceId`: se
 * adopta si es un pedido normal, con su propio grupo (`orderGroupCommerceId ===
 * commerceId`) o sin grupo (antes de oms 1.398.0). El hijo de una venta
 * agrupada que se llama igual lanza `SaleOrderAdoptionConflictError`. Pura.
 */
export function assertAdoptableSingleOrder(existing) {
    if (isSaleGroupChild(existing)) {
        throw adoptionConflict(existing, null, 'child_of_other_sale');
    }
}
/**
 * Antes de crear el hijo 1 de la venta agrupada `groupCommerceId`: `existing`
 * es lo que dio buscar un pedido con `commerceId` = el numero de la venta. Si
 * hay uno, su grupo ya se llama asi y Janis le asociaria los hijos: lanza
 * `SaleOrderAdoptionConflictError` y la venta no se agrupa. Pura.
 */
export function assertSaleGroupCommerceIdFree(existing, groupCommerceId) {
    if (existing !== null && existing !== undefined) {
        throw adoptionConflict(existing, groupCommerceId, 'sale_number_taken');
    }
}
//# sourceMappingURL=sale-group.js.map